import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { productById, collectionById, PRODUCTS } from "../og/data";
import { useLang } from "../og/i18n";
import { useCart } from "../og/cart";
import { Card } from "../og/Products";
import { Marquee } from "../og/Marquee";
import { Line } from "../og/Hero";
import { Crumbs } from "./CollectionPage";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const Gallery = ({ p }) => {
  const [idx, setIdx] = useState(0);
  return (
    <div>
      <div data-testid="product-gallery-main" className="relative aspect-[4/5] overflow-hidden bg-[#E9E9E9] border border-black/10">
        <div className="absolute inset-0 og-card-spot-light" />
        {p.imgs.map((src, k) => (
          <motion.img
            key={src}
            src={src}
            alt={`${p.name} ${k + 1}`}
            initial={false}
            animate={{ opacity: k === idx ? 1 : 0, scale: k === idx ? 1 : 1.04 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className={`absolute inset-0 w-full h-full ${p.fit === "cover" ? "object-cover" : "object-contain p-10"}`}
          />
        ))}
        <div className="absolute bottom-4 left-4 f-mono text-xs tracking-[0.2em] text-black/70">{String(idx + 1).padStart(2, "0")}/{String(p.imgs.length).padStart(2, "0")}</div>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        {p.imgs.map((src, k) => (
          <button
            key={src}
            data-testid={`product-thumb-${k}`}
            onClick={() => setIdx(k)}
            className={`relative aspect-square bg-[#E9E9E9] border-2 transition-colors ${k === idx ? "border-[#FF2A2A]" : "border-transparent hover:border-black/30"}`}
          >
            <img src={src} alt="" className={`absolute inset-0 w-full h-full ${p.fit === "cover" ? "object-cover" : "object-contain p-3"}`} />
          </button>
        ))}
      </div>
    </div>
  );
};

const Options = ({ label, value, items, onPick, render = (x) => x, testid, cols = "" }) => (
  <div>
    <div className="flex justify-between f-mono text-xs tracking-[0.2em] uppercase mb-3">
      <span>{label}</span><span className="text-black/60">{render(value)}</span>
    </div>
    <div className={`flex flex-wrap gap-2 ${cols}`}>
      {items.map((x) => (
        <button
          key={x}
          data-testid={`${testid}-${x}`}
          onClick={() => onPick(x)}
          className={`h-11 min-w-[3.25rem] px-3 f-mono text-xs uppercase border transition-colors ${value === x ? "bg-black text-white border-black" : "border-black/20 hover:border-black"}`}
        >
          {render(x)}
        </button>
      ))}
    </div>
  </div>
);

function ProductView() {
  const { id } = useParams();
  const p = productById(id);
  const { t, tr } = useLang();
  const { add } = useCart();
  const [size, setSize] = useState(() => (p?.sizes.includes("M") ? "M" : p?.sizes[0]));
  const [color, setColor] = useState(p?.colors[0]);
  if (!p) return <Navigate to="/" replace />;

  const coll = collectionById(p.coll);
  const related = [...PRODUCTS.filter((x) => x.coll === p.coll && x.id !== p.id), ...PRODUCTS.filter((x) => x.coll !== p.coll)].slice(0, 3);

  return (
    <main data-testid="product-page" key={p.id} className="bg-[#F5F5F7] text-black">
      <section className="px-5 md:px-10 pt-[130px] md:pt-[150px] pb-20">
        <Crumbs items={[[t("pp.home"), "/"], [coll.title, `/collections/${coll.id}`], [p.name]]} className="mb-8" />
        <div className="grid md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-7"><Gallery p={p} /></div>
          <div className="md:col-span-5 md:sticky md:top-[120px] self-start flex flex-col gap-8">
            <div>
              <Link to={`/collections/${coll.id}`} data-testid="product-collection-link" className="f-mono text-xs tracking-[0.3em] uppercase text-[#FF2A2A] og-link">({coll.tag}) — {coll.title}</Link>
              <h1 data-testid="product-title" className="mt-4 f-disp uppercase text-6xl md:text-[5.5vw] leading-[0.85]">
                <Line ready delay={0.1}>{p.name}</Line>
              </h1>
              <div className="mt-6 flex items-center gap-4">
                <span data-testid="product-page-price" className="f-mono text-2xl tabular-nums">${p.price}.00</span>
                <span className={`f-mono text-[11px] font-bold tracking-[0.15em] px-2 py-1 ${p.pre ? "bg-[#FF2A2A] text-black" : "bg-black text-white"}`}>
                  {p.pre ? t("prod.pre") : t("pp.inStock").toUpperCase()}
                </span>
              </div>
            </div>
            <Options label={t("pp.color")} value={color} items={p.colors} onPick={setColor} render={(x) => t(`c.${x}`)} testid="product-color" />
            <Options label={t("pp.size")} value={size} items={p.sizes} onPick={setSize} testid="product-size" />
            <div>
              <button data-testid="product-add-to-cart" onClick={() => add(p, size, color)} className="og-btn og-btn--red og-btn--ink w-full justify-center !h-16 !text-sm">
                <span>{t("prod.add")} — ${p.price}.00</span>
              </button>
              {p.pre && <p className="mt-3 f-mono text-xs text-black/60">{t("pp.preNote")}</p>}
            </div>
            <Accordion type="single" collapsible defaultValue="desc" className="border-t border-black/15">
              <AccordionItem value="desc" className="border-black/15">
                <AccordionTrigger data-testid="product-desc-toggle" className="f-mono text-xs tracking-[0.2em] uppercase hover:no-underline">{t("pp.desc")}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-black/80">{tr(p.desc)}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="ship" className="border-black/15">
                <AccordionTrigger data-testid="product-ship-toggle" className="f-mono text-xs tracking-[0.2em] uppercase hover:no-underline">{t("pp.ship")}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-black/80">{t("pp.shipText")}</AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      <Marquee items={["WELCOME TO OFF GRID", p.name.toUpperCase(), t("top.ship")]} variant="red" speed={30} />

      <section data-testid="product-related" className="px-5 md:px-10 py-20 md:py-28">
        <h2 className="f-disp uppercase text-5xl md:text-7xl leading-[0.85] mb-12">{t("pp.related")}<span className="text-[#FF2A2A]">.</span></h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14">
          {related.map((x, i) => <Card key={x.id} p={x} i={i} total={related.length} light />)}
        </div>
      </section>
    </main>
  );
}

export default function ProductPage() {
  const { id } = useParams();
  return <ProductView key={id} />;
}
